import React from "react";
import type { ApolloClient } from "@apollo/client";
import { connect, type ConnectedProps } from "react-redux";
import { withApollo, type WithApolloClient } from "@apollo/client/react/hoc";
import { Auth0Context } from "utils/react-auth0-wrapper";
import { Formik } from "formik";
import { push } from "store/actions/router";
import { Row, Col, Form } from "react-bootstrap";
import { get, isEmpty } from "lodash";
import { testSsn, INVALID_SSN_ERROR } from "utils/fieldValidators";
import { createErrorSelector, createLoadingSelector } from "store/selectors";
import { userConstants } from "actions/types";
import {
  IndividualEmailVerificationComplete,
  IndividualLoginCreated,
  IndividualSubmitSsnPending,
} from "statics/states";
import { ScrollToFieldError } from "utils/form";
import LinkSecondaryAuth from "../LinkSecondaryAuth";
import {
  userHasMoreThanOneAuth,
  userIs401kRollIn,
  userIsPaychexSplitIneligible,
  userIsPaychexPerks,
} from "../../../store/selectors/user";
import {
  confirmEmail,
  getUserState,
  getPaychexWorkerRecords,
  submitSsn,
  skipSsn,
} from "actions/userActions";

import InfoTooltip from "components/InfoTooltip";
import Button from "components/Button";
import MaskedInput from "components/MaskedInput";
import Alert from "components/Alert";
import IconInfoCard from "components/IconInfoCard";
import IconSupportCard from "components/IconSupportCard";
import IconSteps from "components/IconSteps";
import IconSpinner from "components/IconSpinner";

import "./IndividualAuthentication.scss";

import * as yup from "yup";

import type { RootState } from "types/state";

interface OwnProps {
  isEmailVerified?: boolean;
  onSubmit?: (...args: unknown[]) => void;
  // Inert props VerifyEmail passes verbatim but this component never reads
  // (preserved from the original markup). companyId/isKelly come from VerifyEmail
  // as lodash get(..., false), so they are widened to accept the false default.
  companyId?: string | boolean;
  requireEin?: boolean;
  isKelly?: unknown;
}

interface FormValues {
  ssn: string;
}

const actions = [userConstants.SUBMIT_SSN, userConstants.SKIP_SSN];

const loadingSelector = createLoadingSelector(actions);

const errorSelector = createErrorSelector(actions);

const mapStateToProps = (state: RootState, ownProps: OwnProps) => {
  const { user } = state;
  const confirmEmailSuccess = state.user.confirmEmailSuccess;
  const userEmailSuccess = state.user.emailConfirmed;

  return {
    user,
    userState: get(state.user, "userState.state", ""),
    confirmEmailSuccess,
    userEmailSuccess,
    ownProps,
    isFetching: loadingSelector(state),
    error: errorSelector(state),
    hasSecondaryAuth: userHasMoreThanOneAuth(state),
    shouldRequireSecondaryAuth: userIs401kRollIn(state),
    isPaychexPerks: userIsPaychexPerks(state),
    isPaychexSplitIneligible: userIsPaychexSplitIneligible(state),
  };
};

const mapDispatchToProps = {
  confirmEmail,
  getUserState,
  getPaychexWorkerRecords,
  submitSsn,
  skipSsn,
  push,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

type Props = OwnProps & WithApolloClient<PropsFromRedux>;

interface State {
  shouldSubmitIdentity: boolean;
  // setState writes `... || this.props.isEmailVerified` (an optional boolean),
  // so this can be undefined; set but never read in render.
  shouldVerifyEmail: boolean | undefined;
  ssn: string;
  isFetchingWorkerRecords: boolean;
}

class IndividualAuthentication extends React.Component<Props, State> {
  static contextType = Auth0Context;
  _isMounted = false;

  constructor(props: Props) {
    super(props);

    this.state = {
      shouldSubmitIdentity: true,
      shouldVerifyEmail: true,
      ssn: "",
      isFetchingWorkerRecords: props.isPaychexPerks,
    };
  }

  componentDidMount = async () => {
    void this.props.getUserState(this.props.client as ApolloClient<object>);

    if (this.props.isPaychexPerks) {
      // getPaychexWorkerRecords resolves on failure too, so the page is never
      // stuck behind the spinner — a failed fetch just renders no disclaimer.
      void this.props
        .getPaychexWorkerRecords(this.props.client as ApolloClient<object>)
        .then(() => {
          this.setState({ isFetchingWorkerRecords: false });
        });
    }
    this.setState({
      shouldVerifyEmail:
        this.props.userState === IndividualLoginCreated ||
        this.props.isEmailVerified,
      shouldSubmitIdentity:
        this.props.userState === IndividualLoginCreated ||
        this.props.userState === IndividualSubmitSsnPending ||
        this.props.userState === IndividualEmailVerificationComplete,
    });
  };

  submitVerification = async ({ ssn }: FormValues) => {
    void this.props
      .submitSsn(this.props.client as ApolloClient<object>, ssn)
      .then(() => {
        window.analytics.track("Submitted Ssn");
        void this.props.getUserState(this.props.client as ApolloClient<object>);
        this.setState({
          shouldSubmitIdentity: false,
        });
        window.analytics.track("Submitted Individual Authentication");
      });
  };

  skipSSN = async () => {
    void this.props
      .skipSsn(this.props.client as ApolloClient<object>)
      .then(() => {
        window.analytics.track("Skipped Ssn");
        void this.props.getUserState(this.props.client as ApolloClient<object>);
      });
  };

  verificationCheckMark = () => {
    return (
      <svg
        className="checkmark"
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 52 52"
      >
        <circle
          className="checkmark__circle"
          cx="26"
          cy="26"
          r="25"
          fill="none"
        />
        <path
          className="checkmark__check"
          fill="none"
          d="M14.1 27.2l7.1 7.2 16.7-16.8"
        />
      </svg>
    );
  };

  buildPageTitle = () => {
    if (this.props.isEmailVerified && !this.state.shouldSubmitIdentity) {
      return "Success!";
    } else if (
      this.props.shouldRequireSecondaryAuth &&
      !this.props.hasSecondaryAuth
    ) {
      return "Welcome to Icon.";
    } else if (
      this.props.shouldRequireSecondaryAuth &&
      this.props.hasSecondaryAuth
    ) {
      return "Next Step: ";
    } else {
      return "Next Steps: ";
    }
  };

  buildPageSubtext = () => {
    if (this.props.isEmailVerified && !this.state.shouldSubmitIdentity) {
      return "";
    } else if (
      this.props.shouldRequireSecondaryAuth &&
      !this.props.hasSecondaryAuth
    ) {
      return "To start your Paychex 401(k) rollover, please create a unique Icon login.";
    } else {
      return "";
    }
  };

  emailTitle = () => {
    if (!this.props.isEmailVerified) {
      return "We sent a link to your email—go there and click the confirm button.";
    } else if (this.props.isEmailVerified) {
      return "Email confirmed.";
    }
  };

  ssnTitle = () => {
    if (!this.props.isEmailVerified) {
      return "Next: Confirm your social security number";
    } else if (this.props.isEmailVerified) {
      return "Confirm your social security number";
    }
  };

  cardData = () => {
    const baseParagraphStyle = {
      fontSize: 14,
    };

    const italicNoteStyle = {
      fontSize: 12,
      fontStyle: "italic",
      marginTop: 5,
    };

    const contentWrapper = (
      children: React.ReactNode,
      customStyle: React.CSSProperties = {}
    ) => (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "8px",
          ...customStyle,
        }}
      >
        {children}
      </div>
    );

    return {
      sections: [
        {
          key: 1,
          title: "About Icon",
          content: contentWrapper(
            <p style={baseParagraphStyle}>
              Icon is a retirement benefits company. We&apos;ve made it easy to
              roll over your 401(k) into a professionally managed IRA—so your
              savings stay invested and working for you.
            </p>
          ),
        },
        {
          key: 2,
          title: "A Rollover IRA",
          content: contentWrapper(
            <p style={baseParagraphStyle}>
              A rollover IRA is a retirement account that allows you to move
              money from your former employer-sponsored plan to an IRA—tax and
              penalty-free—while keeping your money&apos;s tax-advantaged
              status.
            </p>,
            { alignItems: "flex-start", marginBottom: "8px" }
          ),
        },
        {
          key: 3,
          title: "Access to your money",
          content: contentWrapper(
            <>
              <p style={baseParagraphStyle}>
                Consolidate your assets or supplement your company retirement
                plan. Big life events: Withdraw penalty-free for certain
                expenses—like a first home, a birth or college expenses.
              </p>
              <p style={italicNoteStyle}>
                (Check with the IRS for more details.)
              </p>
            </>,
            { alignItems: "flex-start", marginBottom: "8px" }
          ),
        },
      ],
    };
  };

  secondaryAuthHeader = () => {
    if (!this.props.shouldRequireSecondaryAuth || this.props.hasSecondaryAuth) {
      return null;
    }

    return (
      <div className="secondary-auth-header">
        <span className="secondary-auth-header-logos">
          <img className="icon-logo" src="/assets/paychex_logo_v2.svg" />
          <span className="secondary-auth-header-plus">+</span>
          <img className="icon-logo" src="/assets/icon_logo_v2.svg" />
        </span>

        <p className="secondary-auth-header-text">
          Icon is a retirement benefits company and a trusted partner of
          Paychex.
        </p>
      </div>
    );
  };

  render() {
    const identityKey = "ssn";

    // Paychex Flex (Perks): the contractor disclaimer below is driven off the
    // worker records fetched on mount, so hold the page until they land rather
    // than render it once without the disclaimer and again with it.
    if (this.state.isFetchingWorkerRecords) {
      return (
        <div className="individual-authentication">
          {this.secondaryAuthHeader()}
          <div className="perks-worker-records-loading">
            <IconSpinner centered />
          </div>
        </div>
      );
    }

    const shouldSkipSSN =
      this.props.shouldRequireSecondaryAuth &&
      this.props.hasSecondaryAuth &&
      this.props.isEmailVerified;

    /*
     * TODO this should require secondary auth will be based on set paramaters, once we know what those are
     * */
    const shouldRequireSecondaryAuth = this.props.shouldRequireSecondaryAuth;
    const disable =
      !this.props.isEmailVerified ||
      (shouldRequireSecondaryAuth && !this.props.hasSecondaryAuth);

    const shouldDisplayButton =
      this.props.isEmailVerified && !shouldRequireSecondaryAuth;

    const schema = yup.object({
      ssn: yup
        .string()
        .required()
        // PRESERVED BUG (conversion-only): passing testSsn directly hands yup's
        // TestContext to the truthy allowNoDash parameter, silently loosening
        // the SSN format check. Locked by characterization tests.
        // @ts-expect-error -- testSsn's (value, allowNoDash?) signature is not a
        // valid yup TestFunction; this mismatch is the preserved bug itself.
        .test("ssn", INVALID_SSN_ERROR, testSsn)
        .label("Social Security Number"),
    });

    let showEmailVerifyStatus = shouldRequireSecondaryAuth
      ? this.props.hasSecondaryAuth
      : true;

    const showSteps =
      this.props.hasSecondaryAuth || !this.props.shouldRequireSecondaryAuth;

    return (
      <div className="individual-authentication">
        {this.secondaryAuthHeader()}
        <Row
          style={{
            display: "flex",
            justifyContent: "space-between",
            paddingTop: 50,
          }}
        >
          <Col lg={6}>
            <div>
              <p className="page-title">{this.buildPageTitle()}</p>
              <p className="page-subtext">{this.buildPageSubtext()}</p>
            </div>

            {/* TEMPORARY COPY — pending final wording from product, which so
                far only speaks to the contractor case and not to the person
                whose only worker record is inactive. */}
            {this.props.isPaychexSplitIneligible && (
              <div className="perks-contractor-disclaimer">
                <p className="perks-contractor-disclaimer-title">
                  About paycheck splitting
                </p>
                <p className="perks-contractor-disclaimer-text">
                  Paycheck splitting doesn’t currently support independent
                  contractors. You can still sign up, but you won’t have access
                  to the Paychex Perks paycheck split, instead you’ll fund your
                  retirement account manually from your bank account.
                </p>
              </div>
            )}

            {shouldRequireSecondaryAuth && (
              <>
                {!this.props.hasSecondaryAuth && (
                  <Col lg={10} style={{ padding: 0 }}>
                    <LinkSecondaryAuth />
                  </Col>
                )}
              </>
            )}

            <>
              <Formik
                validateOnChange={true}
                validateOnBlur={true}
                validationSchema={schema}
                enableReinitialize={true}
                onSubmit={this.submitVerification}
                initialValues={{
                  ssn: "",
                }}
              >
                {({
                  handleSubmit,
                  handleChange,
                  handleBlur,
                  values,
                  touched,
                  errors,
                }) => (
                  <Form noValidate onSubmit={handleSubmit}>
                    <ScrollToFieldError />

                    {showSteps && (
                      <div style={{ marginTop: 30 }}>
                        <IconSteps
                          stepLabel={this.emailTitle()}
                          stepNumber={1}
                          isActive={
                            (this.props.userState === IndividualLoginCreated ||
                              !this.props.isEmailVerified) &&
                            (!shouldRequireSecondaryAuth ||
                              this.props.hasSecondaryAuth)
                          }
                          isVerified={
                            this.props.isEmailVerified && showEmailVerifyStatus
                          }
                        />

                        {shouldSkipSSN && (
                          <div
                            className="submit-row"
                            style={{ marginLeft: 40, paddingBottom: 40 }}
                          >
                            {this.props.error && (
                              <Alert type="error" msg={this.props.error} />
                            )}

                            <Button
                              withArrow={true}
                              name="submit"
                              loading={this.props.isFetching}
                              onClick={this.skipSSN}
                            />
                          </div>
                        )}

                        {!this.props.shouldRequireSecondaryAuth && (
                          <>
                            <IconSteps
                              stepNumber={2}
                              stepLabel={this.ssnTitle()}
                              isActive={this.props.isEmailVerified}
                              isVerified={!errors.ssn && !isEmpty(values.ssn)}
                              form={
                                <>
                                  {this.props.isEmailVerified && (
                                    <>
                                      <Form.Label>
                                        Social security number{" "}
                                        <InfoTooltip
                                          tooltipBody="It’s required by law to verify your identity to open your IRA. Your information is encrypted and securely stored."
                                          placement="right"
                                        />
                                      </Form.Label>
                                      <Row className="mb-3">
                                        <Form.Group
                                          as={Col}
                                          md={5}
                                          sm={12}
                                          controlId="formBasicSsnField"
                                        >
                                          <Form.Control
                                            as={MaskedInput}
                                            disabled={disable}
                                            mask="000-00-0000"
                                            name="ssn"
                                            placeholder="000-00-0000"
                                            value={values.ssn}
                                            onAccept={(
                                              val: unknown,
                                              mask: unknown
                                            ) =>
                                              handleChange({
                                                target: {
                                                  name: (
                                                    mask as {
                                                      el: {
                                                        input: HTMLInputElement;
                                                      };
                                                    }
                                                  ).el.input.name,
                                                  value: val,
                                                },
                                              } as unknown as React.ChangeEvent<HTMLInputElement>)
                                            }
                                            onBlur={handleBlur}
                                            className="password__input"
                                            isInvalid={
                                              touched.ssn && !!errors.ssn
                                            }
                                            isValid={touched.ssn && !errors.ssn}
                                          />
                                          <Form.Control.Feedback type="invalid">
                                            {errors.ssn}
                                          </Form.Control.Feedback>
                                        </Form.Group>
                                      </Row>
                                    </>
                                  )}
                                </>
                              }
                            />
                          </>
                        )}
                      </div>
                    )}

                    <div
                      className="submit-row"
                      style={{ marginLeft: 40, paddingBottom: 40 }}
                    >
                      {this.props.error && (
                        <Alert type="error" msg={this.props.error} />
                      )}

                      {shouldDisplayButton ? (
                        <Button
                          disabled={
                            disable ||
                            !!errors[identityKey] ||
                            isEmpty(values[identityKey])
                          }
                          withArrow={true}
                          name="submit"
                          loading={this.props.isFetching}
                        />
                      ) : null}
                    </div>
                  </Form>
                )}
              </Formik>
            </>
          </Col>
          <Col lg={4}>
            <IconInfoCard
              // @ts-expect-error -- preserved behavior: cardData() returns no
              // `title` key, so this is `undefined` at runtime (pre-existing).
              title={this.cardData().title}
              // @ts-expect-error -- preserved behavior: cardData() sections use
              // numeric keys and JSX content, not IconInfoCard's string shape.
              sections={this.cardData().sections}
              chevron={true}
            />
            <div style={{ marginTop: 40 }}>
              <IconSupportCard />
            </div>
          </Col>
        </Row>
      </div>
    );
  }
}

export default connector(withApollo<Props>(IndividualAuthentication));
